import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import apiPath from '../../utils/apiPath';


const BASE_URL = '/api/v1.0/orders';

// ── Fetch All (paginated + filtered) ───────────────────────────────────────
export const fetchOrders = createAsyncThunk(
  'orderManagement/fetchAll',
  async (
    { page = 1, limit = 10, status = '', type = '', item_code = '', date_range = '' } = {},
    { rejectWithValue }
  ) => {
    try {
      let url = `${BASE_URL}?page=${page}&limit=${limit}`;
      if (status)     url += `&status=${encodeURIComponent(status)}`;
      if (type)       url += `&type=${encodeURIComponent(type)}`;
      if (item_code)  url += `&item_code=${encodeURIComponent(item_code)}`;
      if (date_range) url += `&date_range=${encodeURIComponent(date_range)}`;
      const response = await apiPath.get(url);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// ── Fetch By ID ────────────────────────────────────────────────────────────
export const fetchOrderById = createAsyncThunk(
  'orderManagement/fetchById',
  async (id, { rejectWithValue }) => {
    try {
      const response = await apiPath.get(`${BASE_URL}/${id}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// ── Create ─────────────────────────────────────────────────────────────────
export const createOrder = createAsyncThunk(
  'orderManagement/create',
  async (orderData, { rejectWithValue }) => {
    try {
      const response = await apiPath.post(`${BASE_URL}/create`, orderData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// ── Update ─────────────────────────────────────────────────────────────────
export const updateOrder = createAsyncThunk(
  'orderManagement/update',
  async ({ id, orderData }, { rejectWithValue }) => {
    try {
      const response = await apiPath.put(`${BASE_URL}/${id}`, orderData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// ── Delete ─────────────────────────────────────────────────────────────────
export const deleteOrder = createAsyncThunk(
  'orderManagement/delete',
  async (id, { rejectWithValue }) => {
    try {
      await apiPath.delete(`${BASE_URL}/${id}`);
      return id;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// ── Slice ──────────────────────────────────────────────────────────────────
const orderManagementSlice = createSlice({
  name: 'orderManagement',
  initialState: {
    orders:       [],
    currentOrder: null,
    loading:      false,
    error:        null,
    totalRecord:  0,
    totalPage:    0,
    currentPage:  1,
    perPage:      10,
  },
  reducers: {
    clearOrderError:   (state) => { state.error = null; },
    setCurrentPage:    (state, action) => { state.currentPage = action.payload; },
    clearCurrentOrder: (state) => { state.currentOrder = null; },
  },
  extraReducers: (builder) => {
    builder
      // ── Fetch All ──────────────────────────────────────────────────────
      .addCase(fetchOrders.pending, (state) => {
        state.loading = true;
        state.error   = null;
      })
      .addCase(fetchOrders.fulfilled, (state, action) => {
        state.loading     = false;
        state.orders      = action.payload.records || [];
        state.totalRecord = action.payload.totalRecord || 0;
        state.totalPage   = action.payload.totalPage   || 0;
        state.currentPage = action.payload.currentPage || 1;
        state.perPage     = action.payload.perPage     || 10;
      })
      .addCase(fetchOrders.rejected, (state, action) => {
        state.loading = false;
        state.error   = action.payload || action.error.message;
      })

      // ── Fetch By ID ────────────────────────────────────────────────────
      .addCase(fetchOrderById.pending,   (state) => { state.loading = true; })
      .addCase(fetchOrderById.fulfilled, (state, action) => {
        state.loading      = false;
        state.currentOrder = action.payload?.details || action.payload;
      })
      .addCase(fetchOrderById.rejected, (state, action) => {
        state.loading = false;
        state.error   = action.payload || action.error.message;
      })

      // ── Create ─────────────────────────────────────────────────────────
      .addCase(createOrder.pending,   (state) => { state.loading = true; })
      .addCase(createOrder.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload?.id) {
          state.orders      = [action.payload, ...state.orders];
          state.totalRecord = state.totalRecord + 1;
        }
      })
      .addCase(createOrder.rejected, (state, action) => {
        state.loading = false;
        state.error   = action.payload || action.error.message;
      })

      // ── Update ─────────────────────────────────────────────────────────
      .addCase(updateOrder.pending,   (state) => { state.loading = true; })
      .addCase(updateOrder.fulfilled, (state, action) => {
        state.loading = false;
        const updated = action.payload;
        const index   = state.orders.findIndex((r) => r.id === updated.id);
        if (index !== -1) state.orders[index] = updated;
        if (state.currentOrder?.id === updated.id) state.currentOrder = updated;
      })
      .addCase(updateOrder.rejected, (state, action) => {
        state.loading = false;
        state.error   = action.payload || action.error.message;
      })

      // ── Delete ─────────────────────────────────────────────────────────
      .addCase(deleteOrder.pending,   (state) => { state.loading = true; })
      .addCase(deleteOrder.fulfilled, (state, action) => {
        state.loading     = false;
        state.orders      = state.orders.filter((r) => r.id !== action.payload);
        state.totalRecord = Math.max(0, state.totalRecord - 1);
      })
      .addCase(deleteOrder.rejected, (state, action) => {
        state.loading = false;
        state.error   = action.payload || action.error.message;
      });
  },
});

export const { clearOrderError, setCurrentPage, clearCurrentOrder } =
  orderManagementSlice.actions;
export default orderManagementSlice.reducer;